import classnames from 'classnames';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';

import { type Board } from '@air/api/types';

import { canMoveBoardsToBoard } from '~/components/Shared/Drag/DNDParentBoard/methods';
import {
  boardDragTypes,
  DragType,
  type DropData,
  type DropLocation,
  isBoardDragType,
  isGetBoardDragItem,
  type RearrangableItemType,
} from '~/components/Shared/Drag/dragTypes';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { selectedBoardsSelector } from '~/store/selectedItems/selectors';
import { canAddAssetsToBoard, canCreateBoard, canMoveAssetsToBoard } from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export type DNDParentBoardInterface = Pick<Board, 'id' | 'ancestors' | 'title'>;

export type DNDParentBoardProps = {
  board: DNDParentBoardInterface;
  children: ReactNode;
  dropLocation: DropLocation;
  style?: CSSProperties;
};

export const DNDParentBoard = ({ board, children, dropLocation, style }: DNDParentBoardProps) => {
  const [canDrop, setCanDrop] = useState(false);
  const store = useAirStore();
  const selectedBoards = selectedBoardsSelector(store.getState());
  const { getBoardPermissions } = useBoardPermissionsCache();

  const checkIfCanDrop = (monitor: DropTargetMonitor<RearrangableItemType>) => {
    // this comes from the `getItem` of DNDMovableToBoardItem
    const item = monitor.getItem();

    const permissions = getBoardPermissions(board.id);

    // this function determines whether or not the dragged item can be dropped here
    // we only want to check if there are selected boards or the user has physically dragged a board
    if ((isBoardDragType(item.type) || selectedBoards.length) && canCreateBoard(permissions)) {
      const boardsToCheck =
        isGetBoardDragItem(item) && item.firstItem ? [...selectedBoards, item.firstItem] : selectedBoards;

      return canMoveBoardsToBoard(boardsToCheck, board);
    } else {
      return canAddAssetsToBoard(permissions) || canMoveAssetsToBoard(permissions);
    }
  };

  const [{ isOver }, drop] = useDrop({
    accept: [...boardDragTypes, DragType.asset, DragType.file],
    drop: (): DropData => ({
      type: 'move',
      location: dropLocation,
      data: board,
    }),
    hover: (_props, monitor) => {
      const canDropItem = checkIfCanDrop(monitor);

      if (canDropItem !== canDrop) {
        setCanDrop(canDropItem);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    canDrop: (_props, monitor) => checkIfCanDrop(monitor),
  });

  return (
    <div
      data-draggable="true"
      className={classnames('group/droppable-item relative h-full', {
        'hovering-drop': isOver && canDrop,
      })}
      style={style}
      ref={drop}
    >
      {children}
    </div>
  );
};
